import type { AreaComp, Comp, GameObj, OpacityComp, PosComp, ZComp } from "kaplay";
import { PX_PER_M } from "../config/constants";
import type { HazardBase } from "../config/types";
import { emit } from "../core/events";
import { k } from "../core/kaplay";
import { heightAboveGround } from "../systems/air";
import type { Agent, RiderView } from "../systems/ai/types";
import { clearsObstacle, impactSeverity } from "../systems/collision";
import type { LookComp } from "./look";
import type { Rider } from "./rider";

export interface HazardSpec<S extends Agent> {
    config: HazardBase;
    step(agent: S, rider: RiderView, dt: number): S;
    onHit(agent: S, rider: RiderView): S;
    canHit?(agent: S): boolean;
}

export interface HazardComp<S extends Agent> extends Comp {
    agent: S;
    hitCooldown: number;
}

type HazardObj<S extends Agent> = GameObj<PosComp | LookComp | ZComp | OpacityComp | AreaComp> & HazardComp<S>;

const HIT_COOLDOWN_S = 1.5;
const DESPAWN_M = 150;

export function riderView(rider: Rider): RiderView {
    const { x, y, speed, heading } = rider.motion;
    const rad = (heading * Math.PI) / 180;
    return { x, y, vx: speed * Math.sin(rad), vy: speed * Math.cos(rad), bellRung: rider.intent.bell };
}

function isFarFromCamera(obj: GameObj<PosComp>): boolean {
    return obj.pos.dist(k.getCamPos()) > DESPAWN_M * PX_PER_M;
}

function hitRider<S extends Agent>(spec: HazardSpec<S>, h: HazardObj<S>, r: Rider, cause: string) {
    if (h.hitCooldown > 0 || spec.canHit?.(h.agent) === false) return;
    if (r.state === "crashed" || r.state === "dead" || r.invulnerable > 0) return;
    if (clearsObstacle(heightAboveGround(r.air), spec.config)) return;

    const severity = impactSeverity(r.motion.speed, spec.config);
    r.motion = { ...r.motion, speed: r.motion.speed * (1 - spec.config.speedLoss) };
    emit("wipeout", { severity, cause });
    if (severity !== "small") r.wipeout(severity);
    h.agent = spec.onHit(h.agent, riderView(r));
    h.hitCooldown = HIT_COOLDOWN_S;
}

export function hazard<S extends Agent>(spec: HazardSpec<S>, initial: S, cause: string): HazardComp<S> {
    return {
        id: "hazard",
        require: ["pos", "look", "z", "opacity", "area"],
        agent: initial,
        hitCooldown: 0,

        add(this: HazardObj<S>) {
            this.onCollideUpdate("player", (other) => hitRider(spec, this, other as Rider, cause));
        },

        update(this: HazardObj<S>) {
            const rider = k.get("player")[0] as Rider | undefined;
            if (!rider) return;
            this.agent = spec.step(this.agent, riderView(rider), k.dt());
            this.hitCooldown = Math.max(0, this.hitCooldown - k.dt());

            this.pos = k.vec2(this.agent.x, this.agent.y).scale(PX_PER_M);
            this.z = this.pos.y;
            this.pose = this.agent.pose;
            this.flipX = this.agent.flipX;
            this.opacity = spec.canHit?.(this.agent) === false ? 0 : 1;

            if (isFarFromCamera(this)) this.destroy();
        },
    };
}
